import { invoke } from "@tauri-apps/api/core";
import { getDeviceSettings } from "../services/deviceSettings";
import { message } from "antd";
import { isTauri } from "../utils/tauri";

export type SaleItem = {
  name: string;
  unit: string;
  price: number;        // cijena sa PDV
  vat: "A" | "B" | "C" | "D" | "E" | string;
  qty: number;
  discount?: number;    // 0-100
};

export type Payment = { type: string; amount: number };
//eslint-disable-next-line
function xmlEscape(s: any) {
  return String(s ?? "")
    .replace(/&/g,"&amp;")
    .replace(/</g,"&lt;")
    .replace(/>/g,"&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g,"&apos;");
}

function buildBaseUrl(ip?: string, port?: number | string) {
  const p = Number(port || 8085);
  if (!ip || !p) throw new Error("Fiskalni IP/port nisu podešeni.");
  return `http://${ip}:${p}`;
}

// --- INIT (isti kao backend): POST /inicijalizacija sa <Operator> ---
async function doInit(baseUrl: string, operator: string, password: string) {
  const xml = `<?xml version="1.0" encoding="utf-8"?>
<Operator xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema">
  <BrojOperatora>${xmlEscape(operator || "0")}</BrojOperatora>
  <Lozinka>${xmlEscape(password || "0")}</Lozinka>
</Operator>`;
  const url = `${baseUrl.replace(/\/+$/,"")}/inicijalizacija`;
  await invoke<string>("tring_http_post_xml", { url, xml });
}

// --- XML gradnja za fiskalni/storno račun (VrstaZahtjeva: 0 = prodaja, 2 = storno) ---
function itemsXml(items: SaleItem[]) {
  return (items || []).map(i => `
    <RacunStavka>
      <artikal>
        <Sifra>0</Sifra>
        <Naziv>${xmlEscape(i.name)}</Naziv>
        <JM>${xmlEscape(i.unit || "kom")}</JM>
        <Cijena>${Number(i.price||0).toFixed(2)}</Cijena>
        <Stopa>${xmlEscape(String(i.vat || "E").toUpperCase())}</Stopa>
        <Grupa>0</Grupa>
        <PLU>0</PLU>
      </artikal>
      <Kolicina>${Number(i.qty||1).toFixed(3)}</Kolicina>
      <Rabat>${Number(i.discount||0).toFixed(2)}</Rabat>
    </RacunStavka>`).join("");
}
export async function cashInLocal(kind: "Gotovina"|"Kartica"|"Virman"|"Cek", amount: number) {
  const s = await getDeviceSettings(["fiscal_ip","fiscal_port","fiscal_operator","fiscal_password"]);
  const base = buildBaseUrl(s["fiscal_ip"], s["fiscal_port"]);
  await doInit(base, String(s["fiscal_operator"]||"0"), String(s["fiscal_password"]||"0"));
  const xml = `<?xml version="1.0" encoding="utf-8"?>
<RacunZahtjev xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema">
  <BrojZahtjeva>${Date.now()}</BrojZahtjeva>
  <VrstaZahtjeva>7</VrstaZahtjeva>
  <NoviObjekat><Oznaka>${xmlEscape(kind)}</Oznaka><Iznos>${Number(amount||0).toFixed(2)}</Iznos></NoviObjekat>
</RacunZahtjev>`;
  const url = `${base}/unosnovca`;
  const raw = await invoke<string>("tring_http_post_xml", { url, xml });
  return parseKasaOdgovor(raw);
}

export async function cashOutLocal(kind: "Gotovina"|"Kartica"|"Virman"|"Cek", amount: number) {
  const s = await getDeviceSettings(["fiscal_ip","fiscal_port","fiscal_operator","fiscal_password"]);
  const base = buildBaseUrl(s["fiscal_ip"], s["fiscal_port"]);
  await doInit(base, String(s["fiscal_operator"]||"0"), String(s["fiscal_password"]||"0"));
  const xml = `<?xml version="1.0" encoding="utf-8"?>
<RacunZahtjev xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema">
  <BrojZahtjeva>${Date.now()}</BrojZahtjeva>
  <VrstaZahtjeva>7</VrstaZahtjeva>
  <NoviObjekat><Oznaka>${xmlEscape(kind)}</Oznaka><Iznos>${Number(amount||0).toFixed(2)}</Iznos></NoviObjekat>
</RacunZahtjev>`;
  const url = `${base}/povratnovca`;
  const raw = await invoke<string>("tring_http_post_xml", { url, xml });
  return parseKasaOdgovor(raw);
}

export async function printPeriodicReportLocal(from: Date, to: Date) {
  const s = await getDeviceSettings(["fiscal_ip","fiscal_port"]);
  const base = buildBaseUrl(s["fiscal_ip"], s["fiscal_port"]);
  const fmt = (d: Date) =>
    `${d.getDate()}.${d.getMonth()+1}.${d.getFullYear()} ${d.toLocaleTimeString('hr-HR',{hour12:false})}`;
  const xml = `<?xml version="1.0" encoding="utf-8"?>
<Zahtjev xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema">
  <BrojZahtjeva>${Date.now()}</BrojZahtjeva>
  <VrstaZahtjeva>5</VrstaZahtjeva>
  <Parametri>
    <Parametar><Naziv>DatumOd</Naziv><Vrijednost xsi:type="xsd:string">${fmt(from)}</Vrijednost></Parametar>
    <Parametar><Naziv>DatumDo</Naziv><Vrijednost xsi:type="xsd:string">${fmt(to)}</Vrijednost></Parametar>
  </Parametri>
</Zahtjev>`;
  const url = `${base}/stampatiperiodicniizvjestaj`;
  const raw = await invoke<string>("tring_http_post_xml", { url, xml });
  return parseKasaOdgovor(raw);
}

function paysXml(payments: Payment[]) {
  if (!payments || !payments.length) return "";
  return `<VrstePlacanja>${
    payments.map(p => `
      <VrstaPlacanja>
        <Oznaka>${xmlEscape(p.type || "Gotovina")}</Oznaka>
        <Iznos>${Number(p.amount||0).toFixed(2)}</Iznos>
      </VrstaPlacanja>`
    ).join("")
  }</VrstePlacanja>`;
}

function buildSaleXML(items: SaleItem[], payments: Payment[], stornoOf?: string|number) {
  const broj = Date.now().toString();
  const vrsta = stornoOf ? 2 : 0; // 0 = fiskalni račun, 2 = reklamirani/storno
  const stornoTag = stornoOf ? `<BrojRacuna>${xmlEscape(stornoOf)}</BrojRacuna>` : "";
  return `<?xml version="1.0" encoding="utf-8"?>
<RacunZahtjev xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema">
  <BrojZahtjeva>${broj}</BrojZahtjeva>
  <VrstaZahtjeva>${vrsta}</VrstaZahtjeva>
  <NoviObjekat>
    ${stornoTag}
    <StavkeRacuna>${itemsXml(items)}</StavkeRacuna>
    ${paysXml(payments)}
  </NoviObjekat>
</RacunZahtjev>`;
} 

// --- Parser za <KasaOdgovor> ---
export function parseKasaOdgovor(xml: string) {
  const get = (tag: string) => {
    const m = xml.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`, "i"));
    return m ? m[1].trim() : "";
  };
   const vrsta =
    /<VrstaOdgovora>\s*OK\s*<\/VrstaOdgovora>/i.test(xml) ? "OK" :
    /<VrstaOdgovora>\s*Greska\s*<\/VrstaOdgovora>/i.test(xml) ? "Greska" : "Nepoznato";
  const values: Record<string,string> = {
    BrojFiskalnogRacuna: get("BrojFiskalnogRacuna"),
    Poruka: get("Poruka"),
    OpisGreske: get("OpisGreske"),
  };
  return { vrsta, values, raw: xml };
}

// === JAVNE FUNKCIJE (koriste postojeće Tauri komande iz fiscal.rs) ===

// Prodaja (fiskalni račun)
export async function printFiscalSale(items: SaleItem[], payments: Payment[]) {
  if(isTauri()===false) {
     message.error("Fiskalna funkcionalnost je dostupna samo u Tauri verziji aplikacije.");
     return { vrsta: "OK", values: { OpisGreske: "Nije Tauri verzija" }, raw: "" };
  }

  const s = await getDeviceSettings([
    "fiscal_ip","fiscal_port","fiscal_operator","fiscal_password","fiscal_demo_mode"
  ]);
  const base = buildBaseUrl(s["fiscal_ip"], s["fiscal_port"]);
  if (!s["fiscal_demo_mode"]) {
  await doInit(base, String(s["fiscal_operator"]||"0"), String(s["fiscal_password"]||"0"));
  }
  const xml = buildSaleXML(items, payments?.length ? payments : [{ type: "Gotovina", amount: 0 }]);
  const url = `${base}/stampatifiskalniracun`;
  console.log("Fiskalni račun:", s);
  if (s["fiscal_demo_mode"]) {
    // demo mode - samo logiraj
    console.log("Fiskalni račun (demo):", xml);
   //create message info like prewiew from fiscal lines and everything
   const preview = xml.split("\n").slice(0, 5).join("\n");
   
    message.info("Fiskalni račun (demo): " + preview

    );
    return { vrsta: "OK", values: {}, raw: "" };
  }
  else {
  const raw = await invoke<string>("tring_http_post_xml", { url, xml });
  return parseKasaOdgovor(raw);
  }
}

// Storno (reklamirani račun)
export async function printRefundReceipt(originalFiscalNo: string|number, items: SaleItem[], payments: Payment[] = []) {
  const s = await getDeviceSettings([
    "fiscal_ip","fiscal_port","fiscal_operator","fiscal_password"
  ]);
  const base = buildBaseUrl(s["fiscal_ip"], s["fiscal_port"]);
  await doInit(base, String(s["fiscal_operator"]||"0"), String(s["fiscal_password"]||"0"));

  const xml = buildSaleXML(items, payments, originalFiscalNo);
  const url = `${base}/stampatireklamiraniracun`;
  const raw = await invoke<string>("tring_http_post_xml", { url, xml });
  return parseKasaOdgovor(raw);
}

// X-report (presjek)
export async function printXReportLocal() {
  const s = await getDeviceSettings(["fiscal_ip","fiscal_port"]);
  const baseUrl = buildBaseUrl(s["fiscal_ip"], s["fiscal_port"]);
  const raw = await invoke<string>("tring_x_report", { baseUrl, seq: Date.now() });
  return parseKasaOdgovor(raw);
}

// Z-report (dnevni)
export async function printZReportLocal() {
  const s = await getDeviceSettings(["fiscal_ip","fiscal_port"]);
  const baseUrl = buildBaseUrl(s["fiscal_ip"], s["fiscal_port"]);
  const raw = await invoke<string>("tring_z_report", { baseUrl, seq: Date.now() });
  return parseKasaOdgovor(raw);
}

// Back-compat: ostavi postojeći naziv da ti ne ruši uvoz u PosRegister
export const fiscalizeLocal = printFiscalSale;
